import React, { useEffect, useState } from "react";

import heroPhones from "../../../assets/images/projects/traveloka-flexible-discovery/hero-phones.png";
import mapEntryFlow from "../../../assets/images/projects/traveloka-flexible-discovery/map-entry-flow.png";
import calendarComponentSpec from "../../../assets/images/projects/traveloka-flexible-discovery/calendar-component-spec.png";
import calendarIteration from "../../../assets/images/projects/traveloka-flexible-discovery/calendar-iteration.png";
import endToEndHandoff from "../../../assets/images/projects/traveloka-flexible-discovery/end-to-end-handoff.png";
import attributeMapping from "../../../assets/images/projects/traveloka-flexible-discovery/attribute-mapping.png";
import destinationTagExercise from "../../../assets/images/projects/traveloka-flexible-discovery/destination-tag-exercise.png";

import { ProjectDetailProps } from "../props";
import {
  Appendix,
  AppendixGrid,
  Connector,
  DecisionList,
  ExplorerCard,
  ExplorerColumn,
  ExplorerGrid,
  ExplorerHeader,
  ExplorerLabel,
  ExplorerSummary,
  Eyebrow,
  FindingOption,
  FindingOptions,
  HeroBody,
  HeroCopy,
  HeroHeading,
  HeroImage,
  HeroStage,
  Lightbox,
  LightboxClose,
  Metric,
  MetricBoard,
  MetricCopy,
  ProofCard,
  Question,
  ResearchExplorer,
  ResearchInner,
  ResponseCard,
  Walkthrough,
  WalkthroughBody,
  WalkthroughCopy,
  WalkthroughInner,
  WalkthroughMeta,
  WalkthroughTab,
  WalkthroughTabs,
  WalkthroughVisual,
  ZoomHint,
} from "./flexible-discovery-style";

type StoryKey = "map" | "time" | "context";

const stories: Record<
  StoryKey,
  {
    signal: string;
    finding: string;
    response: string;
    summary: string;
    title: string;
    copy: string;
    source: string;
    sourceAlt: string;
  }
> = {
  map: {
    signal: "No destination yet",
    finding: "Users treated the map as a place to explore",
    response: "Make “Anywhere” a clear path into map-based search",
    summary: "The map helped users find possible destinations",
    title: "Start with the map",
    copy:
      "During testing, the map was more useful as a search tool than as a preview. We connected it to the “Anywhere” field, then kept the departure point, fare, and trip type visible in the results.",
    source: mapEntryFlow,
    sourceAlt:
      "Original Figma flow from Anywhere and Open via Map to destination recommendations",
  },
  time: {
    signal: "Dates are flexible",
    finding: "People planned around weekends, holidays, and trip length",
    response: "Put exact day, date range, and anytime inside the calendar",
    summary: "The calendar helped users check whether a trip was possible",
    title: "Keep flexibility familiar",
    copy:
      "Users could complete the task with a separate month selector, but it did not match how they normally planned a trip. I kept the familiar calendar and added flexible date types, round-trip information, and fare feedback inside it.",
    source: calendarIteration,
    sourceAlt:
      "Original Figma comparison of the tested month selector and iterated flexible calendar",
  },
  context: {
    signal: "A place looks interesting",
    finding: "An image and price did not explain why the destination fit",
    response: "Add destination tags, activities, and clearer fare context",
    summary: "Destination context helped users understand each option",
    title: "Give each place a reason",
    copy:
      "Some users thought the banner price was for a travel package. We added city tags and examples of activities to explain the recommendation, while keeping the flight fare clear.",
    source: destinationTagExercise,
    sourceAlt:
      "Original Figma destination-tag exercise showing attributes such as Family Fun and Nightlife",
  },
};

const order: StoryKey[] = ["map", "time", "context"];

const VisualLightbox = ({
  source,
  alt,
  onClose,
}: {
  source: string;
  alt: string;
  onClose: () => void;
}) => {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <Lightbox role="dialog" aria-modal="true" aria-label="Full design visual">
      <LightboxClose type="button" onClick={onClose} autoFocus>
        Close
      </LightboxClose>
      <img src={source} alt={alt} />
    </Lightbox>
  );
};

const FindingExplorer = () => {
  const [active, setActive] = useState<StoryKey>("map");
  const story = stories[active];

  return (
    <ResearchExplorer>
      <ResearchInner>
        <ExplorerHeader>
          <h3>Three things became clear from testing.</h3>
          <p>Hover, focus, or tap a signal to follow the finding.</p>
        </ExplorerHeader>
        <ExplorerGrid>
          <ExplorerColumn>
            <ExplorerLabel>User signal</ExplorerLabel>
            <FindingOptions>
              {order.map((key) => (
                <FindingOption
                  key={key}
                  type="button"
                  aria-pressed={active === key}
                  onMouseEnter={() => setActive(key)}
                  onFocus={() => setActive(key)}
                  onClick={() => setActive(key)}
                >
                  {stories[key].signal}
                </FindingOption>
              ))}
            </FindingOptions>
          </ExplorerColumn>
          <Connector aria-hidden="true" />
          <ExplorerColumn aria-live="polite">
            <ExplorerLabel>What research revealed</ExplorerLabel>
            <ExplorerCard>
              <strong>{story.finding}</strong>
              <p>
                Finishing the task was only one part of the test. We also looked
                at the moments when users understood the screen differently
                from what we intended.
              </p>
            </ExplorerCard>
          </ExplorerColumn>
          <Connector aria-hidden="true" />
          <ExplorerColumn>
            <ExplorerLabel>Design response</ExplorerLabel>
            <ResponseCard>
              <strong>{story.response}</strong>
              <p>
                I kept each response connected to the existing flight search,
                so users did not need to learn a completely separate journey.
              </p>
            </ResponseCard>
          </ExplorerColumn>
        </ExplorerGrid>
        <ExplorerSummary>
          <strong>{story.summary}.</strong> The three parts worked together to
          help users move from an early idea to a flight they could search.
        </ExplorerSummary>
      </ResearchInner>
    </ResearchExplorer>
  );
};

const SolutionWalkthrough = () => {
  const [active, setActive] = useState<StoryKey>("map");
  const [zoomed, setZoomed] = useState(false);
  const story = stories[active];

  return (
    <>
      <Walkthrough>
        <WalkthroughInner>
          <WalkthroughTabs aria-label="Solution chapters">
            {order.map((key, index) => (
              <WalkthroughTab
                key={key}
                type="button"
                aria-pressed={active === key}
                onClick={() => setActive(key)}
              >
                0{index + 1} · {key === "context" ? "Destination context" : key}
              </WalkthroughTab>
            ))}
          </WalkthroughTabs>
          <WalkthroughBody>
            <WalkthroughCopy aria-live="polite">
              <h3>{story.title}</h3>
              <p>{story.copy}</p>
              <WalkthroughMeta>
                <div>
                  <dt>Finding</dt>
                  <dd>{story.finding}</dd>
                </div>
                <div>
                  <dt>Outcome</dt>
                  <dd>{story.response}</dd>
                </div>
              </WalkthroughMeta>
            </WalkthroughCopy>
            <WalkthroughVisual>
              <button
                type="button"
                onClick={() => setZoomed(true)}
                aria-label={`Open full ${story.title} visual`}
              >
                <img src={story.source} alt={story.sourceAlt} loading="lazy" />
                <ZoomHint>View full visual</ZoomHint>
              </button>
              <figcaption>Original project visual · select another chapter above</figcaption>
            </WalkthroughVisual>
          </WalkthroughBody>
        </WalkthroughInner>
      </Walkthrough>
      {zoomed && (
        <VisualLightbox
          source={story.source}
          alt={story.sourceAlt}
          onClose={() => setZoomed(false)}
        />
      )}
    </>
  );
};

const ImplementationAppendix = () => {
  const [selected, setSelected] = useState<null | {
    source: string;
    alt: string;
  }>(null);
  const proofs = [
    {
      title: "Attribute mapping",
      description: "Reusing Experience-team data for an MVP.",
      source: attributeMapping,
      alt: "Original Figma board mapping attraction attributes to destination recommendations",
    },
    {
      title: "Component behavior",
      description: "Calendar states for one-way and round-trip use.",
      source: calendarComponentSpec,
      alt: "Detailed original Figma handoff for flexible calendar component behavior",
    },
    {
      title: "End-to-end handoff",
      description: "Entry points, map behavior, filters, and results.",
      source: endToEndHandoff,
      alt: "Wide original Figma handoff covering form, map, filters, and result states",
    },
  ];

  return (
    <>
      <Appendix>
        <summary>See the implementation details</summary>
        <AppendixGrid>
          {proofs.map((proof) => (
            <ProofCard
              key={proof.title}
              type="button"
              onClick={() => setSelected({ source: proof.source, alt: proof.alt })}
            >
              <img src={proof.source} alt="" loading="lazy" />
              <strong>{proof.title}</strong>
              <span>{proof.description}</span>
            </ProofCard>
          ))}
        </AppendixGrid>
      </Appendix>
      {selected && (
        <VisualLightbox
          source={selected.source}
          alt={selected.alt}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
};

export default {
  title: "Open Search for Flexible Travelers",
  timeline: "",
  context:
    "Traveloka's flight search already worked well when users knew their destination and dates. In this project, we explored how Traveloka could also help people who wanted to travel but were still flexible about where and when to go.",
  team: [
    "Visual UI Designers: Freddy and Fariz",
    "Design PIC: Pru",
    "Project Managers: Alvina Rosabella and Astra Lindi",
  ],
  thumbnail: "",
  contents: [
    {
      left: {
        title: "The opportunity came before flight search",
        content: (
          <div>
            <p>
              A normal flight search asks users where they want to go and when
              they want to travel. This works well when the plan is already
              clear. But some users only know that they want to take a trip.
            </p>
            <p>
              From here, I saw an opportunity to help flexible users explore
              their options without changing the familiar search for people
              who already had a plan.
            </p>
          </div>
        ),
      },
      right: {
        title: "Design question",
        content: (
          <Question>
            How might we help flexible travelers decide where and when to go,
            then continue to a flight they can book?
          </Question>
        ),
      },
      bottomContent: (
        <HeroStage>
          <HeroCopy>
            <Eyebrow>Traveloka · Flight discovery</Eyebrow>
            <HeroHeading>Open the search before the plan is fixed.</HeroHeading>
            <HeroBody>
              We used the map to open more possibilities, the calendar to help
              users check their time, and destination information to help them
              choose.
            </HeroBody>
          </HeroCopy>
          <HeroImage
            src={heroPhones}
            alt="Three original Traveloka Figma screens showing search, map discovery, and round-trip results"
          />
        </HeroStage>
      ),
    },
    {
      left: {
        title: "The research showed two different needs",
        content: (
          <div>
            <p>
              Most people who used the standard form already had a specific
              city in mind. They usually found inspiration earlier from
              friends, social media, or their own research.
            </p>
            <p>
              However, when the destination or time was still open, users liked
              exploring through the map. This showed us that Open Search should
              support an earlier planning moment, not replace the normal flight
              search.
            </p>
          </div>
        ),
      },
      right: {
        title: "What we looked for",
        content: (
          <p>
            We did not only check whether users could finish each task. We also
            noted the moments when they misunderstood a price, expected more
            information, or used the screen in a different way from our plan.
          </p>
        ),
      },
      bottomContent: <FindingExplorer />,
    },
    {
      left: {
        title: "Turning the findings into one flow",
        content: (
          <div>
            <p>
              I focused the solution on three connected parts: make “Anywhere”
              useful, bring flexible time into the calendar, and explain why a
              destination might be relevant.
            </p>
            <p>
              You can use the walkthrough below to see how each finding shaped
              the design.
            </p>
          </div>
        ),
      },
      right: {
        title: "The progression",
        content: (
          <DecisionList>
            <li>The map helps users find possible destinations.</li>
            <li>The calendar helps users adjust the trip to their time.</li>
            <li>Destination information helps users understand the options.</li>
          </DecisionList>
        ),
      },
      bottomContent: <SolutionWalkthrough />,
    },
    {
      left: {
        title: "Checking the idea with the available data",
        content: (
          <div>
            <p>
              Destination tags raised a practical question. We needed to know
              who would define and maintain them for every city. I worked with
              the PIC to check whether we could use attraction data that the
              Experience team already had.
            </p>
            <p>
              We grouped the available attributes into five categories: Family
              Fun, Sightseeing & Culture, Events, Nature, and Food & Drink. This
              made the idea more feasible for an MVP. We also split the handoff
              into phases instead of asking the team to build everything at
              once.
            </p>
          </div>
        ),
      },
      right: {
        title: "The first phase focused on the entry point",
        content: (
          <DecisionList>
            <li>Create visible entry points into Open Search.</li>
            <li>Connect an interactive map to usable results.</li>
            <li>Improve region and city-level result logic.</li>
            <li>Leave deeper calendar and filtering work for later phases.</li>
          </DecisionList>
        ),
      },
      bottomContent: <ImplementationAppendix />,
    },
    {
      left: {
        title: "Outcome",
        content: (
          <div>
            <p>
              After the first entry points were activated, visits to Open
              Search increased by more than two times. Visits from Open Search
              into the main flight results also increased by more than two
              times.
            </p>
            <p>
              The rollout also surfaced a smaller lesson: an animated entry
              point hurt loading performance. Replacing it with a static image
              reduced the load while keeping the route discoverable.
            </p>
          </div>
        ),
      },
      right: {
        title: "What changed",
        content: (
          <p>
            Open Search worked as a bridge between early exploration and the
            existing flight search. It helped users build a clearer plan, then
            continue to the main results when they were ready.
          </p>
        ),
      },
      bottomContent: (
        <MetricBoard>
          <Metric>
            <span>Result after the first phase</span>
            <strong>&gt;2×</strong>
          </Metric>
          <MetricCopy>
            <h3>More visits into Open Search and onward to flight results.</h3>
            <p>
              Internal experiment result from the project handover. The source
              material did not include an absolute baseline, so the portfolio
              keeps this claim directional.
            </p>
          </MetricCopy>
        </MetricBoard>
      ),
    },
    {
      left: {
        title: "What I learned",
        content: (
          <div>
            <p>
              My main learning was that the solution did not come from one
              screen. It came from connecting the research with interaction
              details, visual clarity, available data, and a realistic handover
              scope.
            </p>
          </div>
        ),
      },
      right: {
        title: "Explore the source",
        content: (
          <p>
            This is a concise retelling of the project. The complete design and
            edge cases remain available in the{" "}
            <a
              href="https://www.figma.com/design/vT5ng2qA5Zo3nEuoZh2F6f/Portfolio?node-id=203-944&t=P3OM8wQcQZhFODS5-1"
              target="_blank"
              rel="noreferrer noopener"
              style={{ color: "var(--project-link)" }}
            >
              Figma file
            </a>
            .
          </p>
        ),
      },
    },
  ],
} as ProjectDetailProps;
